import { can, formatDate } from '@clim/client-core';
import { COMPLIANCE_LABELS, DOC_REVIEW_LABELS, type Compliance, type ComplianceStatus, type ContractorDocTypeRecord, type ContractorDocumentRecord, type TypeCompliance } from '@clim/domain';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';
import { Empty, ErrorBox, Field, Input, Modal, Textarea } from './ui';

export const COMPLIANCE_TONE: Record<ComplianceStatus, string> = { ok: 'success', expiring: 'warning', expired: 'danger', missing: 'danger' };
export function ComplianceBadge({ status }: { status: ComplianceStatus | null }) {
  if (!status) return <span className="badge tone-neutral">Facultatif</span>;
  return <span className={`badge tone-${COMPLIANCE_TONE[status]}`}>{COMPLIANCE_LABELS[status]}</span>;
}

export interface UserDocs {
  user: { id: string; name: string; isContractor: boolean; company?: string | null; siret?: string | null };
  types: ContractorDocTypeRecord[];
  documents: ContractorDocumentRecord[];
  compliance: Compliance | null;
}

export function useUserDocs(userId: string) {
  const { api } = useReady();
  const online = useOnline();
  const [data, setData] = useState<UserDocs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    if (!online) return;
    api.request<UserDocs>('GET', `/v1/contractors/${userId}/documents`).then(
      (d) => (setData(d), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, online, userId]);
  useEffect(reload, [reload]);
  return { data, error, reload, online };
}

/** Fiche documents d'un sous-traitant : état par type, dépôt, consultation, contrôle (admin). */
export function ContractorDocsPanel({ userId, self }: { userId: string; self: boolean }) {
  const { me, api } = useReady();
  const { data, error, reload, online } = useUserDocs(userId);
  const [upload, setUpload] = useState<TypeCompliance | null>(null);
  const [review, setReview] = useState<ContractorDocumentRecord | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const manager = can(me, 'contractor_document.manage');
  if (!online) return <Empty>Les documents se consultent et se déposent avec une connexion.</Empty>;
  if (!data) return <ErrorBox message={error} />;
  const open = async (id: string) => {
    try {
      const { url } = await api.request<{ url: string }>('GET', `/v1/contractor-documents/${id}/url`);
      window.open(api.resolveUrl(url), '_blank', 'noopener');
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  const c = data.compliance;
  return (
    <div>
      {c && c.status !== 'ok' && (
        <div className="card legal-alert" role="alert">
          <strong>
            {self ? 'Vos documents professionnels ne sont pas à jour' : 'Documents professionnels non à jour'} : {c.issues} document(s) à fournir ou à renouveler.
          </strong>
          <div className="muted">Aucune fonctionnalité n’est bloquée ; merci de régulariser dès que possible.</div>
        </div>
      )}
      {data.user.company && (
        <p>
          🏢 Société : <strong>{data.user.company}</strong>
          {data.user.siret ? ` · SIRET ${data.user.siret}` : ''}
        </p>
      )}
      {!data.user.isContractor && <p className="muted">Ce compte n’est pas marqué « sous-traitant » : les documents sont facultatifs.</p>}
      <ErrorBox message={err} />
      <ul className="plain">
        {(c?.types ?? data.types.filter((t) => t.isActive).map((t) => ({ typeId: t.id, label: t.label, required: t.required, status: null, document: null, toReview: false, daysLeft: null }))).map((t) => {
          const history = data.documents.filter((d) => d.typeId === t.typeId && d.id !== t.document?.id);
          return (
            <li key={t.typeId} className="row doc-row">
              <div className="row-main">
                <strong>
                  {t.label} {t.required && <span className="muted small">(obligatoire)</span>}
                </strong>
                {t.document ? (
                  <span className="muted">
                    Déposé le {formatDate(t.document.createdAt)} par {t.document.uploadedByName}
                    {t.document.expiresOn ? ` · expire le ${t.document.expiresOn.split('-').reverse().join('/')}` : ''}
                    {t.daysLeft !== null && t.daysLeft >= 0 && t.status === 'expiring' ? ` (dans ${t.daysLeft} j)` : ''} · {DOC_REVIEW_LABELS[t.document.review]}
                    {t.document.reviewComment ? ` — ${t.document.reviewComment}` : ''}
                  </span>
                ) : (
                  <span className="muted">Aucun document</span>
                )}
                {history.length > 0 && (
                  <details>
                    <summary className="muted small">Historique ({history.length})</summary>
                    <ul className="plain">
                      {history.map((h) => (
                        <li key={h.id} className="small">
                          <button className="linkish" onClick={() => void open(h.id)}>
                            {formatDate(h.createdAt)}
                          </button>{' '}
                          · {DOC_REVIEW_LABELS[h.review]}
                          {h.expiresOn ? ` · exp. ${h.expiresOn.split('-').reverse().join('/')}` : ''}
                          {h.reviewComment ? ` — ${h.reviewComment}` : ''}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <ComplianceBadge status={t.status} />
              <div className="actions wrap">
                {t.document && (
                  <button className="btn small" onClick={() => void open(t.document!.id)}>
                    Voir
                  </button>
                )}
                {manager && t.document?.review === 'to_review' && (
                  <button className="btn small primary" onClick={() => setReview(t.document)}>
                    Contrôler
                  </button>
                )}
                {(self || manager) && (
                  <button className={`btn small${!t.document || (t.status && t.status !== 'ok') ? ' primary' : ''}`} onClick={() => setUpload(t)}>
                    {t.document ? 'Mettre à jour' : 'Déposer'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {upload && <UploadDialog userId={userId} type={data.types.find((x) => x.id === upload.typeId)!} onClose={() => setUpload(null)} onDone={reload} />}
      {review && <ReviewDialog doc={review} onClose={() => setReview(null)} onDone={reload} />}
    </div>
  );
}

function UploadDialog({ userId, type, onClose, onDone }: { userId: string; type: ContractorDocTypeRecord; onClose(): void; onDone(): void }) {
  const { api } = useReady();
  const file = useRef<HTMLInputElement>(null);
  const [expiresOn, setExpiresOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    const f = file.current?.files?.[0];
    if (!f) return setError('Choisissez un fichier (PDF ou photo)');
    setBusy(true);
    setError(null);
    try {
      const bytes = new Uint8Array(await f.arrayBuffer());
      const q = new URLSearchParams({ typeId: type.id, ...(expiresOn ? { expiresOn } : {}) });
      await api.request('POST', `/v1/contractors/${userId}/documents?${q}`, bytes);
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={type.label} onClose={onClose}>
      <div className="form">
        <Field label="Fichier (PDF ou photo)">
          <input ref={file} type="file" accept="application/pdf,image/*" capture="environment" aria-label="Fichier" />
        </Field>
        {type.expiry !== 'none' && (
          <Field label={`Date d’expiration${type.expiry === 'required' ? ' (obligatoire)' : ' (facultative)'}`}>
            <Input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
          </Field>
        )}
        <p className="muted">Le document précédent est conservé dans l’historique.</p>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" disabled={busy || (type.expiry === 'required' && !expiresOn)} onClick={() => void send()}>
            {busy ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReviewDialog({ doc, onClose, onDone }: { doc: ContractorDocumentRecord; onClose(): void; onDone(): void }) {
  const { api } = useReady();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const decide = (decision: 'approved' | 'rejected') =>
    api
      .request('POST', `/v1/contractor-documents/${doc.id}/review`, { decision, comment })
      .then(() => (onDone(), onClose()))
      .catch((e) => setError(errorMessage(e)));
  return (
    <Modal title={`Contrôler — ${doc.name}`} onClose={onClose}>
      <div className="form">
        {doc.expiresOn && <p>Date d’expiration déclarée : {doc.expiresOn.split('-').reverse().join('/')}</p>}
        <Field label="Commentaire (obligatoire en cas de refus)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn danger-outline" disabled={comment.trim().length < 3} onClick={() => void decide('rejected')}>
            Refuser
          </button>
          <button className="btn primary" onClick={() => void decide('approved')}>
            Document conforme
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Bandeau d'accueil du sous-traitant : alerte visible, jamais bloquante. */
export function MyDocsBanner() {
  const { data } = useUserDocs('me');
  const c = data?.compliance;
  if (!c || c.status === 'ok') return null;
  return (
    <Link className="card legal-alert docs-banner" to="/mes-documents" role="alert">
      <strong>⚠ Documents professionnels : {c.issues} à fournir ou à renouveler</strong>
      <span className="muted">
        {c.types
          .filter((t) => t.status && t.status !== 'ok')
          .map((t) => `${t.label} (${COMPLIANCE_LABELS[t.status!].toLowerCase()})`)
          .join(' · ')}{' '}
        → Mettre à jour
      </span>
    </Link>
  );
}
